import { SyntheticEvent } from 'react';
import { css } from '@emotion/react';
import { useCopy } from '@frontend/document';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, Heading } from '@frontend/design-system';
import { LocationServiceLocation, LocationServiceLocationItem } from '../../types';
import { StatusDot } from '../atoms';

const styles = {
  infoRow: css`
    .info-row {
      &__label {
        color: ${theme.colors.neutral50};
      }

      &__value {
        color: ${theme.colors.black};
      }
    }
  `,
  infoRowCopyable: css`
    cursor: pointer;
    padding: ${theme.spacing(1)};
    margin: 0 -${theme.spacing(1)};

    &:hover {
      background-color: ${theme.colors.neutral10};
    }
  `,
  copiedText: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  statusRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  heading: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
  accordionHeader: css`
    cursor: pointer;
  `,
};

export type LocationInfoCardProps = {
  location: LocationServiceLocation;
  isCurrentLocation: boolean;
  onChangeLocation: (location: LocationServiceLocationItem) => void;
};
export const LocationInfoCard = ({ location, onChangeLocation, isCurrentLocation }: LocationInfoCardProps) => {
  const onClickStart = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onChangeLocation({ locationId: location.locationId || '', name: location.name });
  };
  return (
    <Accordion startingValue={'details'} variant='hybrid' shouldRenderBody>
      <Accordion.Item value='details'>
        <Accordion.Header as='div' css={styles.accordionHeader}>
          <div className='heading' css={styles.heading}>
            <div>
              <Heading level={3}>{location?.name || location?.slug}</Heading>
            </div>
            {isCurrentLocation ? (
              <>
                <Chip variant='primary'>Current</Chip>
              </>
            ) : (
              <div>
                <Button onClick={onClickStart} variant='tertiary' iconName='arrow-right-small'>
                  Select
                </Button>
              </div>
            )}
          </div>
        </Accordion.Header>
        <Accordion.Body css={{ paddingTop: 0 }}>
          <InfoRow label='Status' canCopy={false}>
            <div css={styles.statusRow}>
              <div>{location.active ? <StatusDot.Success /> : <StatusDot.Neutral />}</div>
              <div>{location.active ? 'Active' : 'Inactive'}</div>
            </div>
          </InfoRow>
          <InfoRow label='Slug'>{location.slug}</InfoRow>
          <InfoRow label='Location Type'>{location.opsType}</InfoRow>
          <InfoRow label='Data Center'>???</InfoRow>
          <InfoRow label='Location ID'>{location.locationId}</InfoRow>
          <InfoRow label='Phone Tenant ID'>{location.phoneTenantId}</InfoRow>
          <InfoRow label='Sales Force Id'>{location.salesforceId || '-'}</InfoRow>
          <InfoRow label='Timezone'>{location.timezone}</InfoRow>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export const InfoRow = ({
  label,
  children,
  canCopy = true,
}: React.PropsWithChildren<{ label: string; canCopy?: boolean }>) => {
  const [wasCopied, copyAction] = useCopy(String(children));

  const onClick = () => {
    if (canCopy) {
      copyAction();
    }
  };
  return (
    <div className='info-row' onClick={onClick} css={[styles.infoRow, canCopy && styles.infoRowCopyable]}>
      <div className='info-row__label'>{label}</div>
      <div className='info-row__value'>
        {wasCopied ? (
          <>
            <div css={styles.copiedText}>
              <Icon size={16} name='check' color='success' />
              Copied!
            </div>
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
